import {
    useCallback,
    useContext,
    useEffect,
    useState
} from "react";
import {
    useWindowDimensions,
    TouchableOpacity,
    ScrollView,
    View
} from "react-native";
import type {
    RoleItem,
    UserItem
} from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitDialog,
    NCoreUIKitToast,
    NCoreUIKitTheme,
    PageContainer,
    StateCard,
    SelectBox,
    TextInput,
    Button,
    Modal,
    Text
} from "ncore-ui-kit";
import {
    RefreshCcwIcon,
    PencilIcon,
    TrashIcon,
    UsersIcon,
    UserIcon
} from "lucide-react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import {
    AuthContext
} from "../../contexts/AuthContext";

const Users = () => {
    const {
        radiuses,
        spaces,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 768;

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        activeOrganizationId,
        user
    } = useContext(AuthContext);

    const [
        previewItem,
        setPreviewItem
    ] = useState<UserItem | null>(null);

    const [
        users,
        setUsers
    ] = useState<UserItem[]>([]);

    const [
        roles,
        setRoles
    ] = useState<RoleItem[]>([]);

    const [
        searchQuery,
        setSearchQuery
    ] = useState("");

    const [
        pagination,
        setPagination
    ] = useState({
        currentPage: 1,
        pageSize: 10
    });

    const [
        modalVisible,
        setModalVisible
    ] = useState(false);

    const [
        editId,
        setEditId
    ] = useState<string | null>(null);

    const [
        formData,
        setFormData
    ] = useState({
        admin_role_id: "",
        name_surname: "",
        password: "",
        username: "",
        address: "",
        email: "",
        phone: "",
        role: ""
    });

    const [
        isSubmitting,
        setIsSubmitting
    ] = useState(false);

    const [
        isRefreshing,
        setIsRefreshing
    ] = useState(false);

    const fetchUsers = useCallback(async () => {
        try {
            const token = await AsyncStorage.getItem("authToken");
            const url = new URL(`${process.env.API_URL}/users`);
            if (activeOrganizationId && user?.role === "root") {
                url.searchParams.append("organization_id", activeOrganizationId);
            }
            const response = await fetch(url.toString(), {
                headers: {
                    ...(activeOrganizationId && user?.role === "root" ? {
                        "x-organization-id": activeOrganizationId
                    } : {}),
                    Authorization: `Bearer ${token}`
                }
            });
            const result = await response.json();
            if (result.status === "success") {
                setUsers(result.data);
            }
        } catch (error) {
            console.error("Kullanıcılar alınamadı:", error);
        }
    }, [
        activeOrganizationId,
        user?.role
    ]);

    const fetchRoles = useCallback(async () => {
        try {
            const token = await AsyncStorage.getItem("authToken");
            const url = new URL(`${process.env.API_URL}/roles`);
            if (activeOrganizationId && user?.role === "root") {
                url.searchParams.append("organization_id", activeOrganizationId);
            }
            const response = await fetch(url.toString(), {
                headers: {
                    ...(activeOrganizationId && user?.role === "root" ? {
                        "x-organization-id": activeOrganizationId
                    } : {}),
                    Authorization: `Bearer ${token}`
                }
            });
            const result = await response.json();
            if (result.status === "success") {
                setRoles(result.data);
            }
        } catch (error) {
            console.error("Roller alınamadı:", error);
        }
    }, [
        activeOrganizationId,
        user?.role
    ]);

    useEffect(() => {
        fetchUsers();
        fetchRoles();
    }, [
        fetchUsers,
        fetchRoles
    ]);

    const mappedRoles = roles.map(r => ({
        ...r,
        __title: r.role_name,
        __key: r.id
    }));

    const handleSave = async () => {
        if (!formData.username || !formData.email || (!editId && !formData.password) || !formData.admin_role_id || !formData.name_surname || !formData.phone || !formData.address) {
            NCoreUIKitToast.open({
                title: localize("users-alert-fields-required")
            });
            return;
        }

        if (isSubmitting) {
            return;
        }

        setIsSubmitting(true);

        try {
            const token = await AsyncStorage.getItem("authToken");
            const url = editId ? `${process.env.API_URL}/users/org-admin/${editId}` : `${process.env.API_URL}/users`;
            const method = editId ? "PUT" : "POST";

            const response = await fetch(url, {
                body: JSON.stringify({
                    ...formData,
                    ...(activeOrganizationId && user?.role === "root" ? {
                        organization_id: activeOrganizationId
                    } : {})
                }),
                headers: {
                    "Content-Type": "application/json",
                    ...(activeOrganizationId && user?.role === "root" ? {
                        "x-organization-id": activeOrganizationId
                    } : {}),
                    Authorization: `Bearer ${token}`
                },
                method
            });

            const result = await response.json();

            if (result.status === "success") {
                setModalVisible(false);
                setEditId(null);
                setFormData({
                    admin_role_id: "",
                    name_surname: "",
                    password: "",
                    username: "",
                    address: "",
                    email: "",
                    phone: "",
                    role: ""
                });
                fetchUsers();
            } else {
                NCoreUIKitToast.open({
                    title: result.message || (result.errors ? result.errors.map((e: { message: string }) => e.message).join(", ") : localize("users-toast-unknown-error"))
                });
            }
        } catch (error) {
            console.error("Kayıt hatası:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = (id: string, name: string) => {
        let isDeleting = false;

        NCoreUIKitDialog.open({
            primaryButtonProps: {
                onPress: async ({
                    closeAnimation
                }) => {
                    if (isDeleting) return;

                    isDeleting = true;

                    try {
                        const token = await AsyncStorage.getItem("authToken");
                        const url = new URL(`${process.env.API_URL}/users/${id}`);
                        if (activeOrganizationId && user?.role === "root") {
                            url.searchParams.append("organization_id", activeOrganizationId);
                        }
                        const response = await fetch(url.toString(), {
                            headers: {
                                "Content-Type": "application/json",
                                ...(activeOrganizationId && user?.role === "root" ? {
                                    "x-organization-id": activeOrganizationId
                                } : {}),
                                Authorization: `Bearer ${token}`
                            },
                            method: "DELETE"
                        });

                        const result = await response.json();

                        if (response.status === 409) {
                            closeAnimation(() => {
                                setTimeout(() => {
                                    let isForceDeleting = false;

                                    NCoreUIKitDialog.open({
                                        primaryButtonProps: {
                                            onPress: ({
                                                closeAnimation: closeForce
                                            }) => {
                                                if (isForceDeleting) return;

                                                isForceDeleting = true;

                                                closeForce(() => {
                                                    fetch(`${process.env.API_URL}/users/${id}?force=true`, {
                                                        headers: {
                                                            "Content-Type": "application/json",
                                                            ...(activeOrganizationId && user?.role === "root" ? {
                                                                "x-organization-id": activeOrganizationId
                                                            } : {}),
                                                            Authorization: `Bearer ${token}`
                                                        },
                                                        method: "DELETE"
                                                    }).then(res => res.json()).then(r => {
                                                        if (r.status === "success") {
                                                            NCoreUIKitToast.open({
                                                                title: localize("users-toast-delete-success")
                                                            });
                                                            fetchUsers();
                                                        } else {
                                                            NCoreUIKitToast.open({
                                                                title: r.message
                                                            });
                                                        }
                                                    }).finally(() => {
                                                        isForceDeleting = false;
                                                    });
                                                });
                                            },
                                            title: localize("users-dialog-delete-force-primary"),
                                            type: "danger"
                                        },
                                        content: localize("users-dialog-delete-force-content", [
                                            result.message
                                        ]),
                                        title: localize("users-dialog-delete-force-title"),
                                        secondaryButtonProps: {
                                            title: localize("users-dialog-delete-cancel"),
                                            onPress: ({
                                                closeAnimation: closeForce
                                            }) => closeForce(),
                                            type: "neutral"
                                        },
                                        variant: "yes-no",
                                        isVisible: true
                                    });
                                }, 300);
                            });
                            return;
                        }

                        if (result.status === "success") {
                            NCoreUIKitToast.open({
                                title: localize("users-toast-delete-success")
                            });

                            fetchUsers();

                            closeAnimation();
                        } else {
                            NCoreUIKitToast.open({
                                title: result.message || localize("users-toast-unknown-error")
                            });
                        }
                    } catch (error) {
                        console.error("Silme hatası:", error);
                    } finally {
                        isDeleting = false;
                    }
                },
                title: localize("users-dialog-delete-primary"),
                type: "danger"
            },
            content: localize("users-dialog-delete-content", [
                name
            ]),
            secondaryButtonProps: {
                title: localize("users-dialog-delete-cancel"),
                onPress: ({
                    closeAnimation
                }) => closeAnimation(),
                type: "neutral"
            },
            title: localize("users-dialog-delete-title"),
            variant: "yes-no",
            isVisible: true
        });
    };

    const filteredUsers = users.filter(u => {
        const query = searchQuery.toLowerCase();
        return u.username.toLowerCase().includes(query) ||
            u.email.toLowerCase().includes(query) ||
            (u.name_surname && u.name_surname.toLowerCase().includes(query));
    });

    const totalPages = Math.ceil(filteredUsers.length / pagination.pageSize);
    const paginatedUsers = filteredUsers.slice((pagination.currentPage - 1) * pagination.pageSize, pagination.currentPage * pagination.pageSize);

    return <PageContainer
        isScrollable={true}
    >
        <View
            style={[
                stylesheet.container,
                {
                    padding: spaces.spacingXl
                }
            ]}
        >
            <View
                style={[
                    stylesheet.headerRow,
                    {
                        flexDirection: isMobile ? "column" : "row",
                        gap: isMobile ? spaces.spacingMd : 0,
                        marginBottom: spaces.spacingXl
                    }
                ]}
            >
                <Text
                    variant="titleLargeSize"
                >
                    {localize("users-page-title")}
                </Text>
                <View
                    style={{
                        flexDirection: isMobile ? "column" : "row",
                        gap: spaces.spacingMd
                    }}
                >
                    <Button
                        icon={({
                            color,
                            size
                        }) => {
                            return <RefreshCcwIcon
                                color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                size={size}
                            />;
                        }}
                        spreadBehaviour={isMobile ? "stretch" : "free"}
                        onPress={async () => {
                            setIsRefreshing(true);
                            await Promise.all([
                                fetchUsers(),
                                fetchRoles()
                            ]);
                            setIsRefreshing(false);
                        }}
                        isLoading={isRefreshing}
                        variant="ghost"
                        type="neutral"
                    />
                    <Button
                        spreadBehaviour={isMobile ? "stretch" : "free"}
                        title={localize("users-page-add-button")}
                        onPress={() => {
                            setEditId(null);
                            setFormData({
                                admin_role_id: "",
                                name_surname: "",
                                role: "admin",
                                password: "",
                                username: "",
                                address: "",
                                email: "",
                                phone: ""
                            });
                            setModalVisible(true);
                        }}
                    />
                </View>
            </View>
            <TextInput
                placeholder={localize("users-search-placeholder")}
                style={{
                    ...stylesheet.searchInput,
                    marginBottom: spaces.spacingXl
                }}
                onChangeText={(text) => {
                    setSearchQuery(text);
                    setPagination(prev => ({
                        ...prev,
                        currentPage: 1
                    }));
                }}
                spreadBehaviour="stretch"
                value={searchQuery}
            />
            {
                filteredUsers.length === 0 ?
                    <StateCard
                        icon={({
                            color,
                            size
                        }: { color: string; size: number }) => {
                            return <UsersIcon
                                color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                size={size}
                            />;
                        }}
                        subTitle={localize("users-list-empty-subtitle")}
                        title={localize("users-list-empty-title")}
                    />
                :
                    <>
                        <ScrollView
                            contentContainerStyle={[
                                stylesheet.listContent,
                                {
                                    paddingBottom: spaces.spacingXl,
                                    gap: spaces.spacingMd
                                }
                            ]}
                            showsVerticalScrollIndicator={false}
                        >
                            {paginatedUsers.map((item) => {
                                return <TouchableOpacity
                                    style={[
                                        stylesheet.itemContainer,
                                        {
                                            backgroundColor: colors.content.container.mid,
                                            flexDirection: isMobile ? "column" : "row",
                                            borderRadius: radiuses.md,
                                            padding: spaces.spacingMd,
                                            gap: spaces.spacingMd
                                        }
                                    ]}
                                    onPress={() => setPreviewItem(item)}
                                    activeOpacity={0.7}
                                    key={item.id}
                                >
                                    <View
                                        style={[
                                            stylesheet.itemContent,
                                            {
                                                width: isMobile ? "100%" : "auto",
                                                gap: spaces.spacingMd
                                            }
                                        ]}
                                    >
                                        <View
                                            style={[
                                                stylesheet.itemIcon,
                                                {
                                                    backgroundColor: colors.content.container.default,
                                                    borderRadius: radiuses.full
                                                }
                                            ]}
                                        >
                                            <UserIcon
                                                color={colors.content.icon.emphasized}
                                                size={24}
                                            />
                                        </View>
                                        <View
                                            style={[
                                                stylesheet.itemInfo,
                                                {
                                                    gap: spaces.spacingSm
                                                }
                                            ]}
                                        >
                                            <Text
                                                variant="titleMediumSize"
                                            >
                                                {item.username} ({item.email})
                                            </Text>
                                            <Text
                                                variant="bodyMediumSize"
                                                color="mid"
                                            >
                                                {localize("users-list-role-label", [
                                                    item.role === "superAdmin" ? localize("users-list-role-superadmin") : localize("users-list-role-admin")
                                                ])}
                                            </Text>
                                        </View>
                                    </View>
                                    <View
                                        style={[
                                            stylesheet.itemActions,
                                            {
                                                flexDirection: isMobile ? "column" : "row",
                                                width: isMobile ? "100%" : "auto",
                                                gap: spaces.spacingMd
                                            }
                                        ]}
                                    >
                                        <Button
                                            icon={({
                                                color,
                                                size
                                            }) => {
                                                return <PencilIcon
                                                    color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                                    size={size}
                                                />;
                                            }}
                                            onPress={() => {
                                                setEditId(item.id);
                                                setFormData({
                                                    admin_role_id: item.admin_role_id || "",
                                                    name_surname: item.name_surname || "",
                                                    address: item.address || "",
                                                    phone: item.phone || "",
                                                    username: item.username,
                                                    role: item.role || "",
                                                    email: item.email,
                                                    password: ""
                                                });
                                                setModalVisible(true);
                                            }}
                                            spreadBehaviour={isMobile ? "stretch" : "free"}
                                            title={localize("users-list-edit-button")}
                                            type="neutral"
                                        />
                                        {item.id !== user?.id && <Button
                                            icon={({
                                                color,
                                                size
                                            }) => {
                                                return <TrashIcon
                                                    color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                                    size={size}
                                                />;
                                            }}
                                            onPress={() => handleDelete(item.id, item.username)}
                                            title={localize("users-dialog-delete-primary")}
                                            spreadBehaviour={isMobile ? "stretch" : "free"}
                                            type="danger"
                                        />}
                                    </View>
                                </TouchableOpacity>;
                            })}
                        </ScrollView>
                        {totalPages > 1 && (
                            <View
                                style={[
                                    stylesheet.paginationContainer,
                                    {
                                        paddingVertical: spaces.spacingMd
                                    }
                                ]}
                            >
                                <View
                                    style={stylesheet.paginationInfo}
                                >
                                    <Text
                                        variant="bodyMediumSize"
                                        color="mid"
                                    >
                                        {localize("users-pagination-info", [
                                            ((pagination.currentPage - 1) * pagination.pageSize + 1).toString(),
                                            Math.min(pagination.currentPage * pagination.pageSize, filteredUsers.length).toString(),
                                            filteredUsers.length.toString()
                                        ])}
                                    </Text>
                                </View>
                                <View
                                    style={[
                                        stylesheet.paginationButtons,
                                        {
                                            gap: spaces.spacingMd
                                        }
                                    ]}
                                >
                                    <Button
                                        isDisabled={pagination.currentPage === 1}
                                        title={localize("users-pagination-prev")}
                                        onPress={() => setPagination(prev => ({
                                            ...prev,
                                            currentPage: prev.currentPage - 1
                                        }))}
                                        type="neutral"
                                    />
                                    <Button
                                        isDisabled={pagination.currentPage >= totalPages}
                                        title={localize("users-pagination-next")}
                                        onPress={() => setPagination(prev => ({
                                            ...prev,
                                            currentPage: prev.currentPage + 1
                                        }))}
                                        type="neutral"
                                    />
                                </View>
                            </View>)}
                    </>
            }
            <Modal
                onOverlayPress={({
                    closeAnimation
                }) => closeAnimation(() => setPreviewItem(null))}
                onClosed={() => setPreviewItem(null)}
                portalName="modal-system"
                isActive={!!previewItem}
                id="user-preview-modal"
            >
                <View
                    style={[
                        stylesheet.modalContent,
                        {
                            backgroundColor: colors.content.container.default,
                            width: isMobile ? "100%" : 500,
                            borderRadius: radiuses.md,
                            padding: spaces.spacingXl
                        }
                    ]}
                >
                    <Text
                        variant="titleLargeSize"
                    >
                        {localize("users-preview-title")}
                    </Text>
                    <ScrollView
                        showsVerticalScrollIndicator={false}
                        style={{
                            flexShrink: 1
                        }}
                    >
                        <View
                            style={[
                                stylesheet.formContainer,
                                {
                                    marginTop: spaces.spacingMd,
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            <Text>{localize("users-preview-username", [
                                previewItem?.username || ""
                            ])}</Text>
                            <Text>{localize("users-preview-name", [
                                previewItem?.name_surname || ""
                            ])}</Text>
                            <Text>{localize("users-preview-email", [
                                previewItem?.email || ""
                            ])}</Text>
                            <Text>{localize("users-preview-phone", [
                                previewItem?.phone || ""
                            ])}</Text>
                            <Text>{localize("users-preview-address", [
                                previewItem?.address || ""
                            ])}</Text>
                            <Text>{localize("users-preview-role", [
                                previewItem?.role === "superAdmin" ? localize("users-list-role-superadmin") : localize("users-list-role-admin")
                            ])}</Text>
                        </View>
                    </ScrollView>
                    <View
                        style={[
                            stylesheet.modalActions,
                            {
                                flexDirection: isMobile ? "column-reverse" : "row",
                                marginTop: spaces.spacingXl,
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        <Button
                            title={localize("users-preview-close")}
                            onPress={() => setPreviewItem(null)}
                            spreadBehaviour="stretch"
                            type="neutral"
                        />
                    </View>
                </View>
            </Modal>
            <Modal
                onOverlayPress={({
                    closeAnimation
                }) => closeAnimation(() => setModalVisible(false))}
                onClosed={() => setModalVisible(false)}
                portalName="modal-system"
                isActive={modalVisible}
                id="users-modal"
            >
                <View
                    style={[
                        stylesheet.modalContent,
                        {
                            backgroundColor: colors.content.container.default,
                            width: isMobile ? "100%" : 500,
                            borderRadius: radiuses.md,
                            padding: spaces.spacingXl
                        }
                    ]}
                >
                    <Text
                        variant="titleLargeSize"
                    >
                        {editId ? localize("users-modal-edit-title") : localize("users-modal-create-title")}
                    </Text>
                    <ScrollView
                        showsHorizontalScrollIndicator={false}
                        showsVerticalScrollIndicator={false}
                        style={[
                            stylesheet.formContainer,
                            {
                                marginTop: spaces.spacingMd,
                                flexShrink: 1
                            }
                        ]}
                        contentContainerStyle={{
                            gap: spaces.spacingMd
                        }}
                    >
                        <TextInput
                            onChangeText={(text: string) => setFormData(prev => ({
                                ...prev,
                                name_surname: text
                            }))}
                            placeholder={localize("users-modal-name-placeholder")}
                            title={localize("users-modal-name-title")}
                            defaultValue={formData.name_surname}
                            spreadBehaviour="stretch"
                        />
                        <TextInput
                            placeholder={localize("users-modal-phone-placeholder")}
                            onChangeText={(text: string) => setFormData(prev => ({
                                ...prev,
                                phone: text
                            }))}
                            title={localize("users-modal-phone-title")}
                            defaultValue={formData.phone}
                            spreadBehaviour="stretch"
                            keyboardType="phone-pad"
                        />
                        <TextInput
                            placeholder={localize("users-modal-address-placeholder")}
                            onChangeText={(text: string) => setFormData(prev => ({
                                ...prev,
                                address: text
                            }))}
                            title={localize("users-modal-address-title")}
                            defaultValue={formData.address}
                            spreadBehaviour="stretch"
                        />
                        <TextInput
                            placeholder={localize("users-modal-username-placeholder")}
                            onChangeText={(text: string) => setFormData(prev => ({
                                ...prev,
                                username: text
                            }))}
                            title={localize("users-modal-username-title")}
                            defaultValue={formData.username}
                            spreadBehaviour="stretch"
                        />
                        <TextInput
                            placeholder={localize("users-modal-email-placeholder")}
                            onChangeText={(text: string) => setFormData(prev => ({
                                ...prev,
                                email: text
                            }))}
                            title={localize("users-modal-email-title")}
                            defaultValue={formData.email}
                            keyboardType="email-address"
                            spreadBehaviour="stretch"
                            autoCapitalize="none"
                        />
                        <TextInput
                            placeholder={editId ? localize("users-modal-password-edit-placeholder") : localize("users-modal-password-create-placeholder")}
                            onChangeText={(text: string) => setFormData(prev => ({
                                ...prev,
                                password: text
                            }))}
                            title={localize("users-modal-password-title")}
                            defaultValue={formData.password}
                            spreadBehaviour="stretch"
                            secureTextEntry={true}
                            variant="hidden"
                        />
                        {formData.role !== "superAdmin" && <View>
                            <SelectBox
                                initialSelectedItems={mappedRoles.filter(r => r.__key === formData.admin_role_id)}
                                placeholder={localize("users-modal-role-placeholder")}
                                key={modalVisible ? editId || "new" : "hidden"}
                                spreadBehaviour={isMobile ? "stretch" : "free"}
                                onChange={(selected) => setFormData(prev => ({
                                    ...prev,
                                    admin_role_id: selected[0]?.__key || ""
                                }))}
                                title={localize("users-modal-role-label")}
                                titleExtractor={(item) => item.__title}
                                keyExtractor={(item) => item.__key}
                                selectSheetProps={{
                                    portalName: "modal-system"
                                }}
                                data={mappedRoles}
                            />
                        </View>}
                    </ScrollView>
                    <View
                        style={[
                            stylesheet.modalActions,
                            {
                                flexDirection: isMobile ? "column-reverse" : "row",
                                marginTop: spaces.spacingXl,
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        <Button
                            onPress={() => setModalVisible(false)}
                            title={localize("users-modal-cancel")}
                            spreadBehaviour="stretch"
                            type="neutral"
                        />
                        <Button
                            title={localize("users-modal-save")}
                            spreadBehaviour="stretch"
                            onPress={handleSave}
                        />
                    </View>
                </View>
            </Modal>
        </View>
    </PageContainer>;
};
export default Users;
